import React, { useCallback, useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    IconButton,
    Box,
    Typography,
    Tooltip,
    Snackbar,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useSelector } from 'react-redux';
import type { RootState } from 'state/store';
import { AssetCarouselDialogProps, Asset } from '../AssetExplorer.types';
import {
    formatFileSize,
    assetSupportsInlineImagePreview,
    buildExplorerPublishedAssetUrl,
    copyTextToClipboard,
} from '../AssetExplorer.api';
import { ThemedCarouselAudio, ThemedCarouselVideo } from './CarouselThemedPlayers';
import {
    carouselFooterCaptionSx,
    carouselFooterIconBtnSx,
    carouselFooterTitleSx,
} from '../assetExplorerTheme';

const carouselArrowSx = {
    position: 'fixed',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1400,
    color: '#fff',
    bgcolor: 'rgba(0,0,0,0.45)',
    '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)', bgcolor: 'rgba(0,0,0,0.2)' },
} as const;

export const AssetCarouselDialog: React.FC<AssetCarouselDialogProps> = ({
    open,
    onClose,
    assets,
    initialIndex,
    onDownload,
}) => {
    const theme = useTheme();
    const assetsS3CdnUrl = useSelector((state: RootState) => state.clientConfig.data?.assetsS3CdnUrl);
    const [index, setIndex] = useState(initialIndex);
    const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            const safe = Math.max(0, Math.min(initialIndex, Math.max(0, assets.length - 1)));
            setIndex(safe);
        }
    }, [open, initialIndex, assets.length]);

    useEffect(() => {
        setCopyFeedback(null);
    }, [index]);

    const goPrev = useCallback(() => {
        setIndex((i) => Math.max(0, i - 1));
    }, []);

    const goNext = useCallback(() => {
        setIndex((i) => Math.min(assets.length - 1, i + 1));
    }, [assets.length]);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            const tgt = e.target as HTMLElement | null;
            if (tgt?.closest?.('.MuiSlider-root')) {
                return;
            }
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setIndex((i) => Math.max(0, i - 1));
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                setIndex((i) => Math.min(assets.length - 1, i + 1));
            } else if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, assets.length, onClose]);

    const asset = assets[index];
    const hasPrev = index > 0;
    const hasNext = index < assets.length - 1;
    const multiple = assets.length > 1;

    const publishedAbsoluteUrl = asset
        ? buildExplorerPublishedAssetUrl(assetsS3CdnUrl, asset.publishedRelativeUrl)
        : null;

    const handleOpenPublishedUrl = useCallback(() => {
        if (!publishedAbsoluteUrl) return;
        window.open(publishedAbsoluteUrl, '_blank', 'noopener,noreferrer');
    }, [publishedAbsoluteUrl]);

    const handleCopyPublishedUrl = useCallback(async () => {
        if (!publishedAbsoluteUrl) return;
        const ok = await copyTextToClipboard(publishedAbsoluteUrl);
        setCopyFeedback(ok ? 'Published URL copied to clipboard' : 'Could not copy URL');
    }, [publishedAbsoluteUrl]);

    const renderPreview = (a: Asset) => {
        if (a.type === 'IMAGE' && a.s3Url && assetSupportsInlineImagePreview(a)) {
            return (
                <Box
                    component="img"
                    src={a.s3Url}
                    alt={a.name}
                    sx={{
                        maxWidth: '100%',
                        maxHeight: 'calc(100vh - 140px)',
                        objectFit: 'contain',
                        borderRadius: 1,
                        border: `2px solid ${alpha(theme.palette.secondary.main, 0.35)}`,
                    }}
                />
            );
        }
        if (a.type === 'VIDEO' && a.s3Url) {
            return <ThemedCarouselVideo key={a._id} src={a.s3Url} mediaKey={a._id} />;
        }
        if (a.type === 'AUDIO' && a.s3Url) {
            return <ThemedCarouselAudio key={a._id} src={a.s3Url} mediaKey={a._id} title={a.name} />;
        }
        const isPdf =
            Boolean(a.s3Url) &&
            (a.mimeType?.toLowerCase().includes('pdf') || a.originalName?.toLowerCase().endsWith('.pdf') || a.name?.toLowerCase().endsWith('.pdf'));
        if (a.type === 'DOCUMENT' && a.s3Url && isPdf) {
            return (
                <Box
                    sx={{
                        width: 'min(95vw, 1200px)',
                        height: 'calc(100vh - 220px)',
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: `2px solid ${alpha(theme.palette.secondary.main, 0.35)}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.96),
                        boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.45)}`,
                    }}
                >
                    <Box
                        component="iframe"
                        src={a.s3Url}
                        title={a.name}
                        sx={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                    />
                </Box>
            );
        }
        return (
            <Box sx={{ textAlign: 'center', px: 3, py: 4, maxWidth: 480 }}>
                <InsertDriveFileIcon sx={{ fontSize: 72, color: 'rgba(255,255,255,0.7)', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                    {a.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mb: 2 }}>
                    {a.type} · {formatFileSize(a.size)}
                </Typography>
                <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2 }}>
                    Preview is not available for this file type. Download to open it.
                </Typography>
                <Tooltip title="Download">
                    <IconButton
                        onClick={() => onDownload(a)}
                        size="small"
                        sx={carouselFooterIconBtnSx(theme)}
                        aria-label="Download"
                    >
                        <DownloadIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        );
    };

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    bgcolor: alpha(theme.palette.common.black, 0.92),
                    backgroundImage: 'none',
                },
            }}
        >
            <IconButton
                onClick={onClose}
                sx={{
                    position: 'fixed',
                    top: 12,
                    right: 12,
                    zIndex: 1500,
                    color: '#fff',
                    bgcolor: 'rgba(0,0,0,0.35)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
                }}
                aria-label="Close"
            >
                <CloseIcon />
            </IconButton>

            {multiple && (
                <>
                    <Tooltip title="Previous (←)" placement="right">
                        <span>
                            <IconButton
                                onClick={goPrev}
                                disabled={!hasPrev}
                                size="large"
                                sx={{ ...carouselArrowSx, left: 12 }}
                                aria-label="Previous asset"
                            >
                                <ChevronLeftIcon sx={{ fontSize: 40 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Next (→)" placement="left">
                        <span>
                            <IconButton
                                onClick={goNext}
                                disabled={!hasNext}
                                size="large"
                                sx={{ ...carouselArrowSx, right: 12 }}
                                aria-label="Next asset"
                            >
                                <ChevronRightIcon sx={{ fontSize: 40 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </>
            )}

            <DialogContent
                sx={{
                    p: 2,
                    pt: 6,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                {asset && (
                    <>
                        <Box
                            key={asset._id}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                minHeight: 0,
                            }}
                        >
                            {renderPreview(asset)}
                        </Box>
                        <Box
                            sx={{
                                flexShrink: 0,
                                mt: 2,
                                textAlign: 'center',
                                maxWidth: '90vw',
                            }}
                        >
                            <Typography variant="subtitle1" sx={carouselFooterTitleSx(theme)} noWrap>
                                {asset.name}
                            </Typography>
                            <Typography variant="caption" sx={carouselFooterCaptionSx(theme)}>
                                {index + 1} / {assets.length} · {asset.type} · {formatFileSize(asset.size)}
                            </Typography>
                            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
                                {publishedAbsoluteUrl && (
                                    <>
                                        <Tooltip title="Open published URL in new tab">
                                            <IconButton
                                                size="small"
                                                onClick={handleOpenPublishedUrl}
                                                sx={carouselFooterIconBtnSx(theme)}
                                                aria-label="Open published URL in new tab"
                                            >
                                                <OpenInNewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Copy published URL">
                                            <IconButton
                                                size="small"
                                                onClick={() => void handleCopyPublishedUrl()}
                                                sx={carouselFooterIconBtnSx(theme)}
                                                aria-label="Copy published URL"
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                )}
                                <Tooltip title="Download">
                                    <IconButton
                                        size="small"
                                        onClick={() => onDownload(asset)}
                                        sx={carouselFooterIconBtnSx(theme)}
                                        aria-label="Download"
                                    >
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </>
                )}
            </DialogContent>
            <Snackbar
                open={Boolean(copyFeedback)}
                message={copyFeedback}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                autoHideDuration={2500}
                onClose={() => setCopyFeedback(null)}
            />
        </Dialog>
    );
};

export default AssetCarouselDialog;
